import { useCallback, useState } from 'react';
import { useAppDispatch } from '@core';
import { clearSelectedSeats } from '@modules-seatSelection';
import { resetOrder } from '@modules-checkout';

type TimeoutPhase = 'payment';

interface UseTimeoutHandlerParams {
  validDateTime: string | null;
}

interface UseTimeoutHandlerReturn {
  handleTimeout: () => void;
  isModalOpen: boolean;
  closeModal: () => void;
  timeoutPhase: TimeoutPhase | null;
}

/**
 * Hook для обработки истечения времени оплаты
 *
 * Инкапсулирует логику:
 * - Сброса Redux state
 * - Очистки выбранных мест
 * - Отображения модального окна
 *
 * @param validDateTime - дата окончания времени на оплату
 * @returns функции и состояние для обработки timeout
 */
export const useTimeoutHandler = ({
  validDateTime,
}: UseTimeoutHandlerParams): UseTimeoutHandlerReturn => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeoutPhase, setTimeoutPhase] = useState<TimeoutPhase | null>(null);

  const handleTimeout = useCallback(() => {
    if (!validDateTime) return;

    setTimeoutPhase('payment');

    // Update Redux state
    dispatch(resetOrder());
    dispatch(clearSelectedSeats());

    // Show timeout modal
    setIsModalOpen(true);
  }, [validDateTime, dispatch]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setTimeoutPhase(null);
  }, []);

  return {
    handleTimeout,
    isModalOpen,
    closeModal,
    timeoutPhase,
  };
};
